/**
 * carts-ts (VPN dev) does not persist pack_id on cart line items yet.
 * We remember pack composition per cart token in sessionStorage and merge on read.
 */

import type { CartEntity } from '@/domain/entities/cart.entity';

const STORAGE_PREFIX = 'storefront_pack_cart_annotations:';

export interface PackLineAnnotation {
  packId: string;
  packItemId?: string;
  productId: string;
  variantId: string;
}

function storageKey(cartToken: string): string {
  return `${STORAGE_PREFIX}${cartToken}`;
}

function readAll(cartToken: string): PackLineAnnotation[] {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const raw = sessionStorage.getItem(storageKey(cartToken));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as PackLineAnnotation[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeAll(cartToken: string, annotations: PackLineAnnotation[]): void {
  if (typeof window === 'undefined') {
    return;
  }
  const key = storageKey(cartToken);
  if (annotations.length === 0) {
    sessionStorage.removeItem(key);
    return;
  }
  sessionStorage.setItem(key, JSON.stringify(annotations));
}

export function rememberPackLines(
  cartToken: string,
  packId: string,
  selections: Array<{ packItemId?: string; productId: string; variantId: string }>,
): void {
  const existing = readAll(cartToken).filter((a) => a.packId !== packId);
  const added: PackLineAnnotation[] = selections.map((s) => ({
    packId,
    packItemId: s.packItemId,
    productId: s.productId,
    variantId: s.variantId,
  }));
  writeAll(cartToken, [...existing, ...added]);
}

export function forgetPack(cartToken: string, packId: string): void {
  writeAll(
    cartToken,
    readAll(cartToken).filter((a) => a.packId !== packId),
  );
}

export function forgetPackLine(
  cartToken: string,
  productId: string,
  variantId: string,
): void {
  writeAll(
    cartToken,
    readAll(cartToken).filter(
      (a) => !(a.productId === productId && a.variantId === variantId),
    ),
  );
}

export function applyPackAnnotations(
  cart: CartEntity,
  cartToken: string,
): CartEntity {
  const annotations = readAll(cartToken);
  if (annotations.length === 0) {
    return cart;
  }

  const used = new Set<number>();

  const items = cart.items.map((item) => {
    if (item.pack_id) {
      return item;
    }

    const matchIndex = annotations.findIndex((annotation, index) => {
      if (used.has(index)) {
        return false;
      }
      if (
        annotation.productId !== item.product_id ||
        annotation.variantId !== item.variant_id
      ) {
        return false;
      }
      if (annotation.packItemId && item.pack_item_id) {
        return annotation.packItemId === item.pack_item_id;
      }
      return true;
    });

    if (matchIndex < 0) {
      return item;
    }

    used.add(matchIndex);
    const annotation = annotations[matchIndex];

    return {
      ...item,
      pack_id: annotation.packId,
      pack_item_id: annotation.packItemId ?? item.pack_item_id,
    };
  });

  return { ...cart, items };
}
